import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import WorkListDrawer from './components/WorkListDrawer';
import WorkListFilterModal, { WorkListFilterSnapshot, CATEGORIES } from './components/WorkListFilterModal';
import { CategoryRow, getCategoryIcon, PatientRow } from './components/WorkListRows';
import { WorkListHeaderSection, WorkListSearchSection } from './components/WorkListToolbar';
import TreatmentApplicationsModal from 'src/modules/nurse/order-medicine';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from 'src/api/config/axiosInstance';
import { useAppSelector } from 'src/app/hooks';
import ByzMuiSnackBar from 'src/components/core-components/mui/ByzMuiSnackBar';
import { toLocalDate } from 'src/utils/date/dateHelper';
import { WORKLIST_TABS, mapWorklistPatientsToUi, matchesTabFilter, normalizeApiText, normalizeForCompare, poppins } from './helpers';
import { ActionState, GroupMode, NurseProcessPatientMeta, Patient, PatientTask, TabKey, WorkListProps, WorklistApiResponse } from './types';
import { useSelectedOffice } from 'src/hooks/useSelectedOffice';
const WorkList: React.FC<WorkListProps> = ({ open, onClose }) => {
    const [activeTab, setActiveTab] = useState<TabKey>('pending');
    const [searchValue, setSearchValue] = useState('');
    const [groupMode, setGroupMode] = useState<GroupMode>('hasta');
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState<WorkListFilterSnapshot | null>(null);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [nursePatientMetaByPtId, setNursePatientMetaByPtId] = useState<Map<string, NurseProcessPatientMeta>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarType, setSnackbarType] = useState<'success' | 'error'>('success');
    const [quickActionLoadingTaskId, setQuickActionLoadingTaskId] = useState<string | null>(null);
    const navigate = useNavigate();

    // Kullanıcının ofis / yetki bilgileri (Nurse list ekranındaki mantıkla aynı)
    const { user } = useAppSelector((state) => state.auth);
    const { effectiveOffId } = useSelectedOffice();

    // Treatment application modal state (açılacak kategori task tipine göre belirlenir)
    const [treatmentModal, setTreatmentModal] = useState<{
        open: boolean;
        patient: Patient | null;
        category: 'all' | 'medicine' | 'other' | 'blood';
        initialDate: Date;
    }>({
        open: false, patient: null, category: 'all', initialDate: new Date(),
    });

    /** Task tipine göre TreatmentApplicationsModal kategori belirler */
    const getTaskCategory = useCallback((task: PatientTask): 'all' | 'medicine' | 'other' | 'blood' => {
        if (task.type === 'medicine') return 'medicine';
        if (task.type === 'digerTedavi') return 'other';
        if (task.label === 'Kan İstekleri') return 'blood';
        return 'other'; // diğer her şey other kategorisine düşer
    }, []);

    const openTreatmentModal = useCallback((task: PatientTask, patient: Patient) => {
        // Bakım Planları: hasta bakım ekranını aç
        if (task.label === 'Bakım Planları') {
            // İş listesi drawer'ını kapat; bakım ekranı ayrı sekmede açılıyor
            onClose();
            navigate(`/patient/patient-care-add/${patient.id}`);
            return;
        }

        const category = getTaskCategory(task);
        const initialDate = new Date();
        setTreatmentModal({ open: true, patient, category, initialDate });
    }, [getTaskCategory, navigate, onClose]);

    // Track which patient rows are expanded
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());

    const togglePatient = useCallback((id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const toggleCategory = useCallback((label: string) => {
        setExpandedCategoryIds((prev) => {
            const next = new Set(prev);
            if (next.has(label)) {
                next.delete(label);
            } else {
                next.add(label);
            }
            return next;
        });
    }, []);

    // Kategori id eşlemesi (label -> CATEGORIES id)
    const categoryLabelToId = useMemo(() => {
        const map = new Map<string, string>();
        CATEGORIES.forEach((c: { id: string; label: string }) => {
            map.set(c.label, c.id);
        });
        return map;
    }, []);

    const getCategoryIdFromTaskLabel = useCallback(
        (label: string): string | undefined => {
            // Önce bire bir eşleşme (örn. "İlaç Tedavileri", "Tetkik Hazırlık", "Ameliyathane"...)
            const direct = categoryLabelToId.get(label);
            if (direct) return direct;

            // Worklist etiketleri ile filtre modal etiketleri arasındaki isim farklarını eşle
            switch (label) {
                case 'Diğer Tedaviler':
                case 'Diğer Tedavileri':
                    return 'diger';
                case 'Kan İstekleri':
                case 'Kan Tedavileri':
                    return 'kan';
                case 'Bakım Planları':
                case 'Bakım Tedavileri':
                    return 'bakim';
                default:
                    return undefined;
            }
        },
        [categoryLabelToId]
    );

    const selectedCategoryIds = useMemo(
        () => new Set(activeFilters?.categories ?? []),
        [activeFilters?.categories]
    );

    const selectedPatientNames = useMemo(
        () => new Set((activeFilters?.patients ?? []).map(normalizeForCompare)),
        [activeFilters?.patients]
    );

    const selectedFloorValues = useMemo(
        () => new Set((activeFilters?.floors ?? []).map(normalizeForCompare)),
        [activeFilters?.floors]
    );

    const selectedDepartmentValues = useMemo(
        () => new Set((activeFilters?.departments ?? []).map(normalizeForCompare)),
        [activeFilters?.departments]
    );

    const selectedDoctorValues = useMemo(
        () => new Set((activeFilters?.doctors ?? []).map(normalizeForCompare)),
        [activeFilters?.doctors]
    );

    // Dinamik tab rozet sayıları
    const tabsWithCounts = useMemo(() => {
        let pending = 0;
        let expired = 0;
        let applied = 0;

        patients.forEach((p) => {
            p.tasks.forEach((t) => {
                if (t.status === 'applied') {
                    applied += 1;
                    return;
                }

                // Tetkik Hazırlık, expired sayısına hiç girmesin
                if (t.status === 'expired') {
                    if (t.label === 'Tetkik Hazırlık') return;
                    expired += 1;
                    return;
                }

                if (t.status === 'pending') {
                    pending += 1;
                }
            });
        });

        return WORKLIST_TABS.map((tab) => {
            if (tab.key === 'pending') {
                return {
                    ...tab,
                    badges: [{ ...tab.badges[0], count: pending }],
                };
            }
            if (tab.key === 'expired') {
                return {
                    ...tab,
                    badges: [{ ...tab.badges[0], count: expired }],
                };
            }
            if (tab.key === 'applied') {
                return {
                    ...tab,
                    badges: [{ ...tab.badges[0], count: applied }],
                };
            }
            if (tab.key === 'all') {
                return {
                    ...tab,
                    badges: [
                        { ...tab.badges[0], count: pending },
                        { ...tab.badges[1], count: expired },
                        { ...tab.badges[2], count: applied },
                    ],
                };
            }
            return tab;
        });
    }, [patients]);

    // Worklist verisini backend'den çek
    const fetchWorklist = useCallback(async () => {
        if (!effectiveOffId) return;

        try {
            setIsLoading(true);
            const API_URL = process.env.REACT_APP_API_URL;
            const response = await axiosInstance.get(`${API_URL}/nurse/api/Worklist/GetWorklist`, {
                params: {
                    offId: effectiveOffId,
                    worklistTypeId: 1, // 1 = Hemşire
                    // startDate / endDate opsiyonel; backend default bugün için dönecek
                },
            });

            const root = response?.data;
            const payload: WorklistApiResponse = root?.data ?? root;

            if (!payload) return;

            const backendPatients = mapWorklistPatientsToUi(payload.patients ?? []);
            setPatients(backendPatients);
        } catch (error) {
            console.error('GetWorklist error:', error);
            setSnackbarType('error');
            setSnackbarMessage('İş listesi yüklenirken bir hata oluştu.');
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    }, [effectiveOffId]);

    const fetchNursePatientMeta = useCallback(async () => {
        if (!effectiveOffId || !user?.id) return;

        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const today = toLocalDate(new Date());
            const tomorrowDate = new Date();
            tomorrowDate.setDate(tomorrowDate.getDate() + 1);
            const tomorrow = toLocalDate(tomorrowDate);

            const response = await axiosInstance.post(`${API_URL}/nurse/api/Nurse/GetNurseProcessList`, {
                offId: effectiveOffId,
                userId: user.id,
                hfId: 0,
                deptId: 0,
                startTime: today,
                endTime: tomorrow,
                isDischarge: '0',
                nurseId: 0,
            });

            const root = response?.data;
            const payload = Array.isArray(root?.data)
                ? root.data
                : Array.isArray(root)
                    ? root
                    : [];

            const map = new Map<string, NurseProcessPatientMeta>();
            payload.forEach((row: Record<string, unknown>) => {
                const ptId = normalizeApiText(row?.ptid ?? row?.PT_ID ?? row?.id);
                if (!ptId) return;

                const name = normalizeApiText(row?.name ?? row?.NAME);
                const surname = normalizeApiText(row?.surname ?? row?.SURNAME);

                map.set(ptId, {
                    floor: normalizeApiText(row?.floorinfo ?? row?.FLOOR_INFO),
                    department: normalizeApiText(row?.deptname ?? row?.DEPT_NAME),
                    doctor: normalizeApiText(row?.drname ?? row?.DR_NAME),
                    patientFullName: `${name} ${surname}`.trim(),
                });
            });

            setNursePatientMetaByPtId(map);
        } catch {
            // If metadata call fails, clear extra filters source and keep worklist usable.
            setNursePatientMetaByPtId(new Map());
        }
    }, [effectiveOffId, user?.id]);

    useEffect(() => {
        if (!open) return;
        fetchWorklist();
        fetchNursePatientMeta();
    }, [open, fetchWorklist, fetchNursePatientMeta]);

    // Filter patients — tab + arama + kategori filtreleri
    const sortedPatients = useMemo(() => {
        const getEarliestMinutes = (tasks: PatientTask[]): number => {
            let min = Number.POSITIVE_INFINITY;
            tasks.forEach((t) => {
                if (!t.time) return;
                const [h, m] = t.time.split(':').map(Number);
                if (!Number.isFinite(h) || !Number.isFinite(m)) return;
                const total = h * 60 + m;
                if (total < min) min = total;
            });
            return min === Number.POSITIVE_INFINITY ? Number.MAX_SAFE_INTEGER : min;
        };

        const copy = [...patients];
        copy.sort((a, b) => {
            // 1) Favoriler en üstte
            if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;

            // 2) En eski işi olan hasta önce
            const aMin = getEarliestMinutes(a.tasks);
            const bMin = getEarliestMinutes(b.tasks);
            if (aMin !== bMin) return aMin - bMin;

            // 3) İsim sırası
            return a.name.localeCompare(b.name, 'tr');
        });
        return copy;
    }, [patients]);

    const patientsWithoutCategoryFilter = sortedPatients.map((p) => {
        const nameMatch = p.name.toLowerCase().includes(searchValue.toLowerCase());
        if (!nameMatch) return null;

        const patientMeta = nursePatientMetaByPtId.get(p.id);
        const patientFullName = normalizeForCompare(patientMeta?.patientFullName || p.name);

        if (selectedPatientNames.size > 0 && !selectedPatientNames.has(patientFullName)) {
            return null;
        }

        if (selectedFloorValues.size > 0) {
            const floor = normalizeForCompare(patientMeta?.floor);
            if (!floor || !selectedFloorValues.has(floor)) return null;
        }

        if (selectedDepartmentValues.size > 0) {
            const department = normalizeForCompare(patientMeta?.department);
            if (!department || !selectedDepartmentValues.has(department)) return null;
        }

        if (selectedDoctorValues.size > 0) {
            const doctor = normalizeForCompare(patientMeta?.doctor);
            if (!doctor || !selectedDoctorValues.has(doctor)) return null;
        }

        const visibleTasks = p.tasks.filter((t) => matchesTabFilter(t, activeTab));
        if (visibleTasks.length === 0) {
            return null;
        }
        return { ...p, tasks: visibleTasks };
    }).filter((p): p is Patient => p !== null);

    const filteredPatients = patientsWithoutCategoryFilter.map((p) => {
        const visibleTasks = p.tasks.filter((t) => {
            // Kategori filtresi: eğer seçili kategori yoksa filtreleme yapma
            if (selectedCategoryIds.size > 0) {
                const catId = getCategoryIdFromTaskLabel(t.label);
                if (!catId || !selectedCategoryIds.has(catId)) return false;
            }

            return true;
        });

        if (visibleTasks.length === 0) return null;
        return { ...p, tasks: visibleTasks };
    }).filter((p): p is Patient => p !== null);

    // Worklist içindeki görünür işlere göre kategori sayıları
    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};

        patientsWithoutCategoryFilter.forEach((p) => {
            p.tasks.forEach((t) => {
                const catId = getCategoryIdFromTaskLabel(t.label);
                if (!catId) return;
                counts[catId] = (counts[catId] ?? 0) + 1;
            });
        });

        return counts;
    }, [patientsWithoutCategoryFilter, getCategoryIdFromTaskLabel]);

    const processedCategories = useMemo(() => {
        const map = new Map<string, Array<{ task: PatientTask; patient: Patient }>>();
        filteredPatients.forEach(p => {
            p.tasks.forEach(t => {
                // Tetkik Hazırlık never appears in expired tab
                if (t.label === 'Tetkik Hazırlık' && activeTab === 'expired') return;
                if (activeTab !== 'all' && t.status !== activeTab) return;
                if (!map.has(t.label)) map.set(t.label, []);
                map.get(t.label)!.push({ task: t, patient: p });
            });
        });
        return Array.from(map.entries()).map(([label, tasks]) => ({ label, tasks }));
    }, [filteredPatients, activeTab]);

    const expandAll = useCallback(() => {
        if (groupMode === 'hasta') {
            setExpandedIds(new Set(filteredPatients.map((p) => p.id)));
        } else {
            setExpandedCategoryIds(new Set(processedCategories.map((c) => c.label)));
        }
    }, [groupMode, filteredPatients, processedCategories]);

    const collapseAll = useCallback(() => {
        if (groupMode === 'hasta') {
            setExpandedIds(new Set());
        } else {
            setExpandedCategoryIds(new Set());
        }
    }, [groupMode]);

    // Derive actionState
    let actionState: ActionState = null;
    if (groupMode === 'hasta') {
        if (filteredPatients.length > 0) {
            const allOpen = filteredPatients.every((p) => expandedIds.has(p.id));
            const allClosed = filteredPatients.every((p) => !expandedIds.has(p.id));
            if (allOpen) {
                actionState = 'open';
            } else if (allClosed) {
                actionState = 'close';
            }
        }
    } else {
        if (processedCategories.length > 0) {
            const allOpen = processedCategories.every((c) => expandedCategoryIds.has(c.label));
            const allClosed = processedCategories.every((c) => !expandedCategoryIds.has(c.label));
            if (allOpen) {
                actionState = 'open';
            } else if (allClosed) {
                actionState = 'close';
            }
        }
    }

    const selectGroup = (mode: GroupMode) => {
        setGroupMode(mode); // always set — radio button behavior, one must always be selected
    };

    const handleQuickAction = useCallback(async (_patientId: string, task: PatientTask) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;
            // Sadece Tetkik (typeId 4) ve Ameliyathane (typeId 5) için quick action
            if (!task.typeId || !task.id) return;
            setQuickActionLoadingTaskId(task.id);

            if (task.typeId === 4) {
                // Tetkik
                const actionStatus: 'SentForTest' | 'AcceptToServiceForTest' =
                    task.actionType === 'tetkikKabul' ? 'AcceptToServiceForTest' : 'SentForTest';

                const response = await axiosInstance.post(`${API_URL}/nurse/api/Worklist/ExecuteTetkikAction`, {
                    worklistitemid: Number(task.id),
                    actionstatus: actionStatus,
                });

                const root = response?.data;
                let message: string = 'Tetkik aksiyonu başarıyla uygulandı.';
                if (root?.data) {
                    if (typeof root.data === 'string') {
                        // data string ise içinden message çekmeyi dene
                        const match = String(root.data).match(/message:\s*([^,}]+)/);
                        if (match && match[1]) {
                            message = match[1].trim();
                        }
                    } else if (typeof root.data === 'object' && root.data.message) {
                        message = root.data.message;
                    }
                }
                setSnackbarType('success');
                setSnackbarMessage(message);
                setSnackbarOpen(true);
            } else if (task.typeId === 5) {
                // Ameliyathane
                let actionStatus: 'PreopPreparationComplete' | 'SendToOperatingRoom' | 'AcceptToService' = 'PreopPreparationComplete';
                if (task.actionType === 'gonder' || task.type === 'surgery') {
                    actionStatus = 'SendToOperatingRoom';
                } else if (task.actionType === 'tetkikKabul') {
                    actionStatus = 'AcceptToService';
                }

                const response = await axiosInstance.post(`${API_URL}/nurse/api/Worklist/ExecuteAmeliyathaneAction`, {
                    worklistitemid: Number(task.id),
                    actionstatus: actionStatus,
                });

                const root = response?.data;
                let message: string = 'Ameliyathane aksiyonu başarıyla uygulandı.';
                if (root?.data) {
                    if (typeof root.data === 'string') {
                        const match = String(root.data).match(/message:\s*([^,}]+)/);
                        if (match && match[1]) {
                            message = match[1].trim();
                        }
                    } else if (typeof root.data === 'object' && root.data.message) {
                        message = root.data.message;
                    }
                }
                setSnackbarType('success');
                setSnackbarMessage(message);
                setSnackbarOpen(true);
            } else {
                return;
            }

            // Backend cache invalidates; listeyi yenile
            fetchWorklist();
        } catch (error: any) {
            // Backend dokümantasyonundaki hata formatını kullan
            const rawMessage: string =
                error?.response?.data?.error ||
                error?.message ||
                '';

            let friendlyMessage = rawMessage;

            const hasNullRef = /Object reference not set to an instance of an object/i.test(rawMessage || '');
            const hasEfSaveError =
                /An error occurred while saving the entity changes/i.test(rawMessage || '') ||
                /See the inner exception for details/i.test(rawMessage || '');

            // Teknik .NET / EF mesajlarını daha sade Türkçe metne çevir
            if (!friendlyMessage || hasNullRef || hasEfSaveError) {
                if (task.typeId === 4) {
                    friendlyMessage = 'Tetkik işlemi sırasında bir hata oluştu.';
                } else if (task.typeId === 5) {
                    friendlyMessage = 'Ameliyathane işlemi sırasında bir hata oluştu.';
                } else {
                    friendlyMessage = 'İşlem sırasında bir hata oluştu.';
                }
            }

            setSnackbarType('error');
            setSnackbarMessage(friendlyMessage);
            setSnackbarOpen(true);
        } finally {
            setQuickActionLoadingTaskId(null);
        }
    }, [fetchWorklist]);

    const handleToggleFavorite = useCallback(async (patientId: string, currentIsFavorite: boolean) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;
            if (!currentIsFavorite) {
                await axiosInstance.post(`${API_URL}/nurse/api/Worklist/AddWorklistFavorite`, {
                    ptid: Number(patientId),
                });
                setSnackbarType('success');
                setSnackbarMessage('Hasta favoriye eklendi');
            } else {
                await axiosInstance.delete(`${API_URL}/nurse/api/Worklist/RemoveWorklistFavorite/${patientId}`);
                setSnackbarType('success');
                setSnackbarMessage('Hasta favoriden kaldırıldı');
            }

            setSnackbarOpen(true);
            fetchWorklist();
        } catch (error) {
            console.error('Worklist favorite toggle error:', error);
            setSnackbarType('error');
            setSnackbarMessage('Favori işlemi sırasında bir hata oluştu.');
            setSnackbarOpen(true);
        } finally {
        }
    }, [fetchWorklist]);

    return (
        <>
            <WorkListDrawer open={open} onClose={onClose}>

                {/* ── BLUE HEADER SECTION ── */}
                <WorkListHeaderSection
                    activeTab={activeTab}
                    tabs={tabsWithCounts}
                    onTabChange={setActiveTab}
                    onFilterClick={() => setIsFilterModalOpen(true)}
                    onClose={onClose}
                />

            {/* ── WHITE/GREY BOTTOM SECTION ── */}
            <Box
                sx={{
                    flex: 1,
                    background: '#F6F6F6',
                    borderRadius: '24px 24px 0 0',
                    marginTop: '-14px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <WorkListSearchSection
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    groupMode={groupMode}
                    actionState={actionState}
                    onSelectGroup={selectGroup}
                    onExpandAll={expandAll}
                    onCollapseAll={collapseAll}
                />

                {/* Patient / Category list — white card */}
                <Box
                    sx={{
                        flex: 1,
                        background: '#fff',
                        borderRadius: '16px 16px 0 0',
                        mx: '10px',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: 3 },
                        '&::-webkit-scrollbar-thumb': { background: '#ddd', borderRadius: 2 },
                        padding: groupMode === 'kategori' ? '12px 10px' : 0,
                    }}
                >
                    {groupMode === 'hasta' ? (
                        <>
                            {isLoading && patients.length === 0 && (
                                <Box sx={{ py: '32px', display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={24} sx={{ color: '#2196F3' }} />
                                </Box>
                            )}
                            {!isLoading && filteredPatients.map((patient) => (
                                <PatientRow
                                    key={patient.id}
                                    patient={patient}
                                    isExpanded={expandedIds.has(patient.id)}
                                    onToggle={() => togglePatient(patient.id)}
                                    onToggleFavorite={handleToggleFavorite}
                                    onDigerTedaviClick={(task: PatientTask) => openTreatmentModal(task, patient)}
                                    onQuickActionClick={handleQuickAction}
                                    quickActionLoadingTaskId={quickActionLoadingTaskId}
                                />
                            ))}
                            {!isLoading && filteredPatients.length === 0 && (
                                <Box sx={{ py: '32px', textAlign: 'center', fontFamily: poppins, fontSize: '13px', color: '#B7B7B7' }}>
                                    Hasta bulunamadı
                                </Box>
                            )}
                        </>
                    ) : (
                        <div style={{ width: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 12, display: 'flex' }}>
                            {isLoading && patients.length === 0 && (
                                <Box sx={{ py: '32px', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={24} sx={{ color: '#2196F3' }} />
                                </Box>
                            )}
                            {!isLoading && processedCategories.map((category) => (
                                <CategoryRow
                                    key={category.label}
                                    label={category.label}
                                    tasks={category.tasks}
                                    isExpanded={expandedCategoryIds.has(category.label)}
                                    onToggle={() => toggleCategory(category.label)}
                                    iconNode={getCategoryIcon(category.label)}
                                    onTaskAction={(task: PatientTask, patient: Patient) => {
                                        if (task.typeId === 4 || task.typeId === 5) {
                                            handleQuickAction(patient.id, task);
                                            return;
                                        }
                                        openTreatmentModal(task, patient);
                                    }}
                                />
                            ))}
                            {!isLoading && processedCategories.length === 0 && (
                                <Box sx={{ py: '32px', width: '100%', textAlign: 'center', fontFamily: poppins, fontSize: '13px', color: '#B7B7B7' }}>
                                    Kategori bulunamadı
                                </Box>
                            )}
                        </div>
                    )}
                    <Box sx={{ height: 24 }} />
                </Box>
            </Box>

            {/* Filter Modal */}
            <WorkListFilterModal
                open={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                categoryCounts={categoryCounts}
                onApply={(filters) => {
                    setActiveFilters(filters);
                    setIsFilterModalOpen(false);
                }}
            />

            {/* Treatment Applications Modal — task tipine göre kategori otomatik seçili açılır */}
            {treatmentModal.patient && (
                <TreatmentApplicationsModal
                    key={`${treatmentModal.patient.id}-${treatmentModal.category}-${treatmentModal.initialDate.toISOString()}`}
                    open={treatmentModal.open}
                    onClose={() => {
                        setTreatmentModal(prev => ({ ...prev, open: false }));
                        fetchWorklist();
                    }}
                    patientId={treatmentModal.patient.id}
                    patientName={treatmentModal.patient.name}
                    initialCategory={treatmentModal.category}
                    initialDate={treatmentModal.initialDate}
                />
            )}

        </WorkListDrawer>

        <ByzMuiSnackBar
            open={snackbarOpen}
            onClose={() => setSnackbarOpen(false)}
            title={snackbarMessage}
            message=""
            type={snackbarType}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            autoHideDuration={3000}
        />
    </>
);
};

export default WorkList;
